import {
    Directive,
    ElementRef,
    forwardRef,
    HostListener,
    Renderer2
} from '@angular/core';
import {
    ControlValueAccessor,
    NG_VALUE_ACCESSOR
} from '@angular/forms';

@Directive({
    selector: '[currencyInput]',
    standalone: true,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CurrencyInputDirective),
            multi: true
        }
    ]
})
export class CurrencyInputDirective implements ControlValueAccessor {
    private onChange = (value: any) => { };
    private onTouched = () => { };

    constructor(private el: ElementRef, private renderer: Renderer2) { }

    @HostListener('input', ['$event.target.value'])
    onInput(value: string): void {
        const cleaned = value.replace(/[^\d,\.]/g, '').replace(',', '.');
        const number = parseFloat(cleaned);
        if (!isNaN(number)) {
            this.onChange(number);
        } else {
            this.onChange(null);
        }
    }

    @HostListener('keydown', ['$event'])
    onKeyDown(event: KeyboardEvent): void {
        const allowedKeys = [
            'Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Home', 'End'
        ];

        if (allowedKeys.includes(event.key)) {
            return;
        }

        const isNumber = /^[0-9]$/.test(event.key);
        const isDecimal = event.key === '.' || event.key === ',';

        if (!isNumber && !isDecimal) {
            event.preventDefault();
        }

        // Evitar más de un separador decimal
        const input = this.el.nativeElement.value;
        if ((event.key === '.' || event.key === ',') && (input.includes('.') || input.includes(','))) {
            event.preventDefault();
        }
    }

    @HostListener('keydown.enter', ['$event'])
    onEnterPress(event: KeyboardEvent): void {
        this.el.nativeElement.blur();
    }

    @HostListener('blur')
    onBlur(): void {
        this.onTouched();
        const value = this.el.nativeElement.value;
        const cleaned = value.replace(/[^\d,\.]/g, '').replace(',', '.');
        const number = parseFloat(cleaned);
        if (!isNaN(number)) {
            const formatted = new Intl.NumberFormat('es-AR', {
                style: 'currency',
                currency: 'ARS',
                minimumFractionDigits: 2
            }).format(number);
            this.renderer.setProperty(this.el.nativeElement, 'value', formatted);
        }
    }

    writeValue(value: number | null): void {
        if (value !== null && !isNaN(value)) {
            const formatted = new Intl.NumberFormat('es-AR', {
                style: 'currency',
                currency: 'ARS',
                minimumFractionDigits: 2
            }).format(value);
            this.renderer.setProperty(this.el.nativeElement, 'value', formatted);
        } else {
            this.renderer.setProperty(this.el.nativeElement, 'value', '');
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.renderer.setProperty(this.el.nativeElement, 'disabled', isDisabled);
    }
}
